import { useId } from 'react';
import { cn } from '@/lib/cn';

interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * An on/off switch.
 *
 * A real checkbox under the paint, not a styled div with a click handler: the
 * checkbox is what carries the state to a screen reader, what the space bar
 * toggles, and what a form autofills. The visible track is drawn from its
 * `peer` state, so the two can never disagree.
 */
export function Toggle({ label, description, checked, disabled = false, onChange }: ToggleProps) {
  const id = useId();
  const descriptionId = `${id}-description`;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm text-surface-200">
          {label}
        </label>
        {description ? (
          <p id={descriptionId} className="mt-0.5 text-xs text-surface-500">
            {description}
          </p>
        ) : null}
      </div>

      <span className="relative inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-describedby={description ? descriptionId : undefined}
          onChange={(event) => onChange(event.target.checked)}
          className="peer size-11 cursor-pointer appearance-none disabled:cursor-not-allowed"
        />

        {/* Drawn on top and non-interactive, so every click lands on the input —
            which is also what keeps the 44px touch target. */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 my-auto h-6 w-11 rounded-full transition',
            'bg-surface-700 peer-checked:bg-brand-500 peer-disabled:opacity-40',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-400',
          )}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 right-[1.375rem] my-auto size-5 rounded-full bg-surface-950 transition-transform',
            'peer-checked:translate-x-[1.25rem] peer-disabled:opacity-40',
          )}
        />
      </span>
    </div>
  );
}
